// "The stars in Islam": what Islam teaches about the stars, astrology and the old names in the sky, and the short
// reminders (tanbīh) on the cards where Ahillah shows the zodiac or a name from the false deities and myths of
// polytheist peoples. The names stay because they are the ones astronomers use everywhere; their myths are never told.
// Quotations, the scholars' among them, come word for word from data-src/islam (islamEvidence.ts), never paraphrased,
// and the origins of the names from names-a.json and names-b.json (asteroids: Schmadel, Dictionary of Minor Planet
// Names). Wording: always "a false deity" that a people worshipped (the owner's choice, 1 October 2026), and the
// transliteration of MuSunnah Publications (data-src/arabian/README.md).

/** The twelve constellations astrology made into "star signs" (by IAU abbreviation). */
export const ZODIAC = new Set(['Ari', 'Tau', 'Gem', 'Cnc', 'Leo', 'Vir', 'Lib', 'Sco', 'Sgr', 'Cap', 'Aqr', 'Psc']);

/** The planets, named after false deities: those the Romans worshipped, and Uranus after one from the Greeks' myths,
 * of whom no worship is recorded. */
const PLANET_DEITIES: Record<string, string> = {
  Mercury: 'a false deity the ancient Romans worshipped', Venus: 'a false deity the ancient Romans worshipped',
  Mars: 'a false deity the ancient Romans worshipped', Jupiter: 'a false deity the ancient Romans worshipped',
  Saturn: 'a false deity the ancient Romans worshipped', Uranus: 'a false deity of the ancient Greeks’ myths',
  Neptune: 'a false deity the ancient Romans worshipped',
  // The dwarf planets, each shown on its page in the Gallery (origins from the IAU's Gazetteer of Planetary Nomenclature).
  Pluto: 'a false deity the ancient Romans worshipped', Ceres: 'a false deity the ancient Romans worshipped',
  Eris: 'a false deity of the ancient Greeks’ myths', Haumea: 'a false deity of the old Hawaiian religion',
  Makemake: 'a false deity the Rapa Nui people of Easter Island worshipped',
};

/** Constellations named after false deities, or after people made into them (Ridpath, Star Tales). */
const DEITY_CONSTELLATIONS: Record<string, string> = {
  Her: 'Hercules, a false deity the ancient Greeks and Romans worshipped',
  Oph: 'a figure the ancient Greeks identified with one of the false deities they worshipped',
  Com: 'Berenice, a queen of Egypt who was worshipped in her lifetime',
};
/** Constellations whose figures come from the myths of the ancient Greeks, tied to the false deities they worshipped. */
const MYTH_CONSTELLATIONS = new Set([
  'And', 'Aql', 'Ara', 'Aur', 'Boo', 'CMa', 'CMi', 'Car', 'Cas', 'Cen', 'Cep', 'Cet', 'CrB', 'Crv', 'Crt', 'Cyg', 'Del', 'Dra',
  'Eri', 'Hya', 'Lep', 'Lup', 'Lyr', 'Ori', 'Peg', 'Per', 'Phe', 'PsA', 'Pup', 'Sge', 'Ser', 'Tri', 'UMa', 'UMi', 'Vel',
]);

/** Named stars whose names come from false deities, or from myths and sacred lore tied to them (names-b.json). Names
 * from folk tales rather than worship (Chalawan, Pipirima, Rapeto) are left out, but for Tianjinnan, which the owner
 * chose to remind (A4, 14 October 2026). */
const GREEK_MYTH = 'the myths of the ancient Greeks, which were tied to the false deities they worshipped';
const STAR_ORIGINS: Record<string, string> = {
  ...Object.fromEntries(['Rigil Kentaurus', 'Menkent', 'Algol', 'Alcyone', 'Atlas', 'Electra', 'Maia', 'Merope', 'Taygeta', 'Celaeno', 'Asterope', 'Pleione', 'Prima Hyadum', 'Secunda Hyadum', 'Baten Kaitos', 'Aiolos'].map((n) => [n, GREEK_MYTH])),
  Ankaa: 'the Arabic name for the phoenix, a bird of ancient myth tied to the worship of the Sun',
  Castor: 'one of the twins Castor and Pollux, false deities the ancient Greeks and Romans worshipped',
  Pollux: 'one of the twins Castor and Pollux, false deities the ancient Greeks and Romans worshipped',
  Antares: 'a comparison with Ares, a false deity the ancient Greeks worshipped',
  Porrima: 'a false deity the ancient Romans worshipped',
  Kornephoros: 'Hercules, a false deity the ancient Greeks and Romans worshipped',
  Antinous: 'a young man whom a Roman emperor made into a false deity',
  Deltoton: 'a triangle the ancient Greeks linked with the name of the chief false deity they worshipped',
  Sargas: 'a weapon of Marduk, a false deity the ancient Babylonians worshipped',
  ...Object.fromEntries(['Uridim', 'Udkadua', 'Sukhurmashu', 'Pagru'].map((n) => [n, 'the myths of the ancient Babylonians, which were tied to the false deities they worshipped'])),
  Pabilsang: 'a false deity the ancient Sumerians worshipped',
  Ninnisig: 'a false deity the ancient Sumerians worshipped',
  ...Object.fromEntries(['Ashlesha', 'Bharani', 'Shatabhisha', 'Shakata', 'Dhanishta'].map((n) => [n, 'one of the lunar mansions of Hindu astrology, each tied to one of the false deities the Hindus worship'])),
  Revati: 'a false deity the Hindus worship, and a lunar mansion of Hindu astrology',
  Paushna: 'a false deity the Hindus worship, and a lunar mansion of Hindu astrology',
  Naga: 'a serpent of Balinese myth',
  Kalasungsang: 'a demon of Balinese belief',
  Ran: 'a false deity of Norse myth',
  Fafnir: 'the myths of the ancient Norse, which were tied to the false deities they worshipped',
  Aurwandilsta: 'the myths of the ancient Norse, which were tied to the false deities they worshipped',
  Khepdenreret: 'a false deity the ancient Egyptians worshipped',
  ...Object.fromEntries(['Kenemu', 'Heryibwia'].map((n) => [n, 'the star lore of the ancient Egyptians, which was tied to the false deities they worshipped'])),
  Taiyi: 'a false deity of the old Chinese state religion',
  Tianyi: 'a false deity of old Chinese astrology',
  Xihe: 'a false deity of the Sun in Chinese myth',
  Tengshe: 'a creature of Chinese legend, set in the sky by ancient Chinese astrologers',
  Tianjinnan: 'a Chinese constellation tied to a folk tale',
  Fenmu: 'a Chinese constellation, the Tomb, among a group tied to the worship of ancestors',
  Tonatiuh: 'the Aztecs’ name for the Sun, which they worshipped as a false deity',
  'Tupã': 'one of the chief false deities the Guarani people worshipped',
  'Bélénos': 'a false deity the ancient Gauls worshipped',
  Kamuy: 'the word the Ainu people use for the spirits they worship',
  Urquchillay: 'a false deity the Inca worshipped',
  Yaqana: 'the myths of the Inca, which were tied to the false deities they worshipped',
  'Enduri Senggu': 'a false deity the Manchu people worshipped',
  ...Object.fromEntries(['Larawag', 'Ginan', 'Wurren', 'Gudja', 'Nganurganity', 'Muning'].map((n) => [n, 'the sacred lore of an Aboriginal Australian people'])),
};
/** Arabic star names built on saʿd, good fortune: the Arabs before Islam took these stars as lucky. */
const FORTUNE_STARS = new Set(['Sadalsuud', 'Sadalmelik', 'Sadachbia', 'Sadalbari', 'Nashira', 'Homam', 'Biham', 'Matar', 'Dabih', 'Alshat']);
/** Names from the Balinese Palelintangan, a calendar for reading character and fortune from the day of birth. */
const DIVINATION_STARS = new Set(['Bade', 'Yuyu', 'Udang', 'Gaja', 'Lumbung', 'Lembu', 'Lintangkumba', 'Puwuh Atarung', 'Hru']);
/** Names of Chinese constellations that astrologers read for omens or held to govern things on Earth (the IAU's notes and
 *  its All Skies Encyclopaedia; data-src/islam/NAMES-B.md): the owner's A4, 14 October 2026. Each finishes "Its name
 *  comes from Chinese astrology, which …". */
const CHINESE_OMENS: Record<string, string> = {
  ...Object.fromEntries(['Yunyu', 'Leidian', 'Pili'].map((n) => [n, 'read omens of rain and of the ruler’s decrees in this group of constellations'])),
  Nandou: 'held this constellation to govern rank, wealth and lifespan',
  Tianyue: 'held this constellation to govern opening and closing',
};
/** The asteroids Ahillah shows, all named from Greek and Roman religion (Schmadel). */
const ROMAN = 'a false deity the ancient Romans worshipped';
const GREEK = 'a false deity the ancient Greeks worshipped';
const ASTEROID_ORIGINS: Record<string, string> = {
  Ceres: ROMAN, Pallas: GREEK, Juno: ROMAN, Vesta: ROMAN, Hebe: GREEK, Iris: GREEK, Flora: ROMAN, Metis: GREEK_MYTH, Hygiea: GREEK,
  Eunomia: GREEK, Psyche: GREEK_MYTH, Melpomene: 'one of the Muses, false deities the ancient Greeks worshipped',
  Amphitrite: GREEK, Nysa: GREEK_MYTH, Eros: GREEK,
};

const TAIL = 'Ahillah uses the name only because it is the one astronomers use everywhere, not out of any belief in the false deities or myths behind it. Allah alone created the heavens, and associating partners with Him is a tremendous wrong.';
/** The same, for a reminder about several names (Phobos and Deimos, the candidate dwarf planets). */
export const TAIL_SEVERAL = 'Ahillah uses the names only because they are the ones astronomers use everywhere, not out of any belief in the false deities or myths behind them. Allah alone created the heavens, and associating partners with Him is a tremendous wrong.';
// "Disbelief (kufr)", not "shirk or kufr": every shirk is kufr, but not every kufr is shirk, so kufr covers both (the owner,
// 15 October 2026).
const ASTROLOGY = 'The stars and planets have no effect on anyone’s character, personality or future: believing they do is disbelief (kufr), against the Islamic creed.';
const FORTUNE = 'The Arabs before Islam took some stars as lucky, but the stars bring neither good nor bad fortune, which comes only by Allah’s decree.';

/** Above the observatories' pictures and news in Learn (the owner's item 17, wording approved 19 October 2026). */
export const NEWS_REMINDER = 'These pictures and stories come from the observatories as they publish them, not from Ahillah. They may give ages for stars and galaxies, or names taken from false deities and myths. Ahillah shows them only for the sky they picture, not for anything they believe. Allah alone created the heavens.';

/** The reminder for a constellation's card, if it needs one. */
export function constellationReminder(id: string): string | null {
  if (ZODIAC.has(id)) {
    return `Astrology made this one of its twelve “star signs”, which began with the Babylonians, who tied these constellations to the false deities they worshipped. ${ASTROLOGY} Reading horoscopes, even for fun, is forbidden.`;
  }
  if (DEITY_CONSTELLATIONS[id]) return `Its name comes from ${DEITY_CONSTELLATIONS[id]}. ${TAIL}`;
  if (MYTH_CONSTELLATIONS.has(id)) return `This figure comes from ${GREEK_MYTH}. ${TAIL}`;
  return null;
}

/** The reminder for a planet named after a false deity, standing in front of a constellation (IAU abbreviation and
 *  name). The Sun and the Moon get none: their cards name no constellation. */
export function bodyReminder(body: string, conId: string, conName: string): string | null {
  const deity = PLANET_DEITIES[body];
  if (!deity) return null;
  const moons = body === 'Jupiter' ? ' Its four large moons are named after figures in the myths of this false deity.' : '';
  return `${body} is named after ${deity}. Saying where it stands${ZODIAC.has(conId) ? `, as in “${body} in ${conName}”,` : ''} is astronomy; astrology, which grew out of the Babylonians’ reading of the sky for messages from the false deities they worshipped, reads meaning into it.${moons} The planets move only by Allah’s command. ${ASTROLOGY}`;
}

/** The reminder for a named star, if its name comes from false deities or myths, or the star was worshipped. Sirius's
 *  gives its verse by id, so the card shows the approved text with its Arabic (Reminder). */
export function starReminder(name: string): string | null {
  if (name === 'Sirius') return 'Some Arabs before Islam worshipped this star, which they called al-Shiʿrá. The Quran names it to declare that Allah is its Lord: {Q-08} Associating partners with Allah is a tremendous wrong.';
  if (FORTUNE_STARS.has(name)) return `Its name comes from an Arabic star name built on saʿd, “good fortune”. ${FORTUNE}`;
  if (DIVINATION_STARS.has(name)) return `Its name comes from a Balinese calendar used to read a person’s character and fortune from their day of birth, a kind of astrology. ${ASTROLOGY}`;
  if (CHINESE_OMENS[name]) return `Its name comes from Chinese astrology, which ${CHINESE_OMENS[name]}. ${ASTROLOGY}`;
  const o = STAR_ORIGINS[name];
  return o ? `Its name comes from ${o}. ${TAIL}` : null;
}

/** The reminder for an asteroid named after a false deity. */
export function asteroidReminder(name: string): string | null {
  const o = ASTEROID_ORIGINS[name];
  return o ? `Its name comes from ${o}. ${TAIL}` : null;
}

/** The planets' large moons whose names come from the myths of the ancient Greeks (the IAU's Gazetteer of Planetary
 * Nomenclature). Those of Uranus are named after characters in Shakespeare and Pope, and need no reminder. */
const GREEK_MOONS = new Set(['Phobos', 'Deimos', 'Io', 'Europa', 'Ganymede', 'Callisto', 'Mimas', 'Enceladus', 'Tethys', 'Dione', 'Rhea', 'Titan', 'Hyperion', 'Iapetus', 'Triton', 'Charon']);

/** The reminder for the Gallery's page of a moon (or of several, as "Phobos and Deimos"). */
export function moonReminder(title: string): string | null {
  const n = (title.match(/\p{Lu}\p{Ll}+/gu) ?? []).filter((w) => GREEK_MOONS.has(w)).length;
  return n ? (n > 1 ? `Their names come from ${GREEK_MYTH}. ${TAIL_SEVERAL}` : `Its name comes from ${GREEK_MYTH}. ${TAIL}`) : null;
}

/** The candidate dwarf planets, named from the false deities and myths of several peoples (the naming citations of the
 * IAU's Minor Planet Center). */
const CANDIDATE_PEOPLES: Record<string, string> = {
  Orcus: 'the ancient Romans', Gonggong: 'the ancient Chinese', Sedna: 'the Inuit', Quaoar: 'the Tongva people of California',
};

/** The reminder for the Gallery's candidate dwarf planets: those named (all of them on their page, one in a picture). */
export function candidatesReminder(names: string[]): string | null {
  const known = names.filter((n) => CANDIDATE_PEOPLES[n]);
  const peoples = [...new Set(known.map((n) => CANDIDATE_PEOPLES[n]))];
  if (!peoples.length) return null;
  const list = peoples.length > 1 ? `${peoples.slice(0, -1).join(', ')} and ${peoples[peoples.length - 1]}` : peoples[0];
  return known.length > 1 ? `Their names come from the false deities and myths of ${list}. ${TAIL_SEVERAL}` : `Its name comes from the false deities and myths of ${list}. ${TAIL}`;
}

/** The reminder for a galaxy, cluster or nebula named from false deities or myths. */
export function dsoReminder(d: { m?: number; name?: string }): string | null {
  if (d.m === 45) return `The name Pleiades comes from ${GREEK_MYTH}. ${TAIL}`;
  const planet = d.name?.match(/^(Jupiter|Saturn)\b/)?.[1];
  if (planet) return `It is named after the planet ${planet}, which is named after ${ROMAN}. ${TAIL}`;
  return null;
}

/** With the Moon's stations (Arabic setting on): the anwāʾ, the stars once thought to bring rain. */
const ANWA_REMINDER = 'Before Islam, many Arabs believed that certain stars, above all those of the Moon’s stations, brought the rain. The Prophet ﷺ related that Allah said: “…whoever said, ‘By the nawʾ (a star’s setting or rising) of such-and-such,’ that one is a disbeliever in Me and a believer in the star.” (Ṣaḥīḥ al-Bukhārī 846; Ṣaḥīḥ Muslim 71) Knowing the stars and the stations to tell the seasons and the time is permitted.';

/** A lunar station's card (Arabic setting on): the anwāʾ, and good fortune for the stations named saʿd. */
export const stationReminder = (tr: string) => (tr.startsWith('Saʿd') ? `${ANWA_REMINDER} Its name is built on saʿd, “good fortune”. ${FORTUNE}` : ANWA_REMINDER);

/** Figures of the Arabian sky, besides the stations, to which poets ascribed rain (names-b.json). */
const ANWA_FIGURES = new Set(['al-dhira-al-mabsuta', 'al-khiba', 'al-dalw']);
export function arabianReminder(id: string, tr: string, manzil?: number): string | null {
  if (manzil) return stationReminder(tr);
  const luck = id.startsWith('sad-') ? `Its name is built on saʿd, “good fortune”. ${FORTUNE}` : '';
  if (ANWA_FIGURES.has(id)) return luck ? `${ANWA_REMINDER} ${luck}` : ANWA_REMINDER;
  return luck || null;
}

/** Keeps an honorific sign (ﷺ, ﵀ …) on the same line as the name before it. */
/**
 * A quotation from the Quran as MuSunnah Publications' books set it: inside ﴿ ﴾, a section mark (۞) left out, and the
 * numbers of the verses within it as plain digits, which the Uthmanic face draws in their rosettes; the number of the
 * last verse is left to the reference.
 */
export function quranText(arabic: string): string {
  const t = arabic.trim().replace(/^۞[\s\u00A0]*/, '').replace(/[\s\u00A0]*(?:﴿[٠-٩]+﴾|[٠-٩]+)$/, '').replace(/\s*﴿([٠-٩]+)﴾/g, '\u00A0$1');
  return `\uFD3F${t}\uFD3E`;
}
export const keepHonorifics = (s: string) => s.replace(/ (?=[\uFD40-\uFD4F\uFDFA])/g, '\u00A0');

/** A paragraph of Ahillah's own words, or a quotation by its id in islamEvidence.ts. */
export type IslamPart = string | { quote: string };
/** `id`: a section other parts of Ahillah open the page at. */
export interface IslamSection { id?: string; title: string; parts: IslamPart[] }
const q = (...ids: string[]): IslamPart[] => ids.map((quote) => ({ quote }));

/** The top of the page (BUILD-SPEC item 43): [Al-Ṣāffāt: 37:6] with al-Saʿdī's explanation. */
export const ISLAM_OPENING: IslamPart[] = q('Q-44', 'SC-38');

/** The page "The stars in Islam": Ahillah's short explanations, with the evidence and the scholars word for word. */
export const ISLAM_SECTIONS: IslamSection[] = [
  {
    title: 'Signs of Allah, not powers',
    parts: [
      'The Sun, the Moon, the planets and the stars are creations of Allah. They move only by His command, and they have no power of their own over anything that happens on Earth.',
      ...q('Q-06', 'Q-25', 'Q-07'),
    ],
  },
  {
    title: 'Astrology and “star signs”',
    parts: [
      'Astrology claims that the stars and planets, or the “sign” a person is born under, shape their character, personality or future. Only Allah knows the unseen.',
      ...q('Q-09', 'H-02', 'H-05', 'H-04'),
      'Ibn ʿUthaymīn ﵀ explains the kinds of knowledge of the stars:',
      ...q('UTH-1'),
      'Ibn Taymiyyah ﵀ was asked about astrologers who sit in the streets and in shops, claiming to tell of hidden things. He answered:',
      ...q('TAY-1', 'TAY-2'),
      'Ibn ʿUthaymīn ﵀ on going to a soothsayer:',
      ...q('UTH-5'),
      'Ibn Bāz ﵀ was asked about what magazines say of people born under each sign:',
      ...q('BAZ-1'),
      'Answering a man whose family believed in astrology, he said:',
      ...q('BAZ-3'),
      'He also wrote:',
      ...q('BAZ-2'),
    ],
  },
  {
    title: 'Rain and the stars',
    parts: [
      'Before Islam, many Arabs believed that the stars of the Moon’s stations brought the rain as they set or rose at dawn (the anwāʾ). Rain comes from Allah alone.',
      ...q('H-01', 'H-03'),
      'Ibn ʿUthaymīn ﵀, explaining the first of these hadith:',
      ...q('UTH-4'),
    ],
  },
  // The shooting-stars article (BUILD-SPEC item 14): the verse, its explanation, the hadith, Qatādah's words and Ibn
  // ʿUthaymīn's on the flames as meteors. (Qatādah's words were in "Signs of Allah, not powers" before; Muslim 2229 was
  // H-08, now the approved H-20.)
  {
    id: 'shooting-stars',
    title: 'Shooting stars',
    parts: q('Q-40', 'SC-25', 'H-20', 'SC-30', 'SC-31'),
  },
  {
    title: 'Using the stars rightly',
    parts: [
      'Learning the stars to find the way, to tell the time and the seasons, and to find the qiblah is permitted.',
      ...q('Q-01', 'Q-02', 'Q-10', 'Q-11'),
      'Ibn ʿUthaymīn ﵀, on the second kind of knowledge of the stars:',
      ...q('UTH-2'),
      'Al-Khaṭṭābī ﵀, explaining the hadith on knowledge of the stars:',
      ...q('KHT-1'),
      'The early scholars differed over learning the Moon’s stations:',
      ...q('S-04', 'S-05', 'S-02'),
      'Ibn ʿUthaymīn ﵀ concludes:',
      ...q('UTH-3'),
    ],
  },
  {
    title: 'The old names in the sky',
    parts: [
      'Many names in the sky come from polytheist peoples. The twelve signs of the zodiac, and reading the sky for omens, began in Babylonia, where the zodiac figures stood for the false deities the Babylonians worshipped. The planets and many constellations carry the names of false deities the Greeks and Romans worshipped, and of figures from their myths.',
      'Ahillah keeps these names only because they are the ones astronomers use everywhere, so that you can find each thing anywhere and in any book. It never tells their myths, and the card of each constellation, planet, asteroid and star with such a name carries a short reminder.',
      'The Quran itself names false deities in order to refute them, and names al-Shiʿrá, Sirius, which some Arabs worshipped, to declare that Allah is its Lord. Associating partners with Allah is a tremendous wrong.',
      ...q('Q-33', 'Q-34', 'Q-08', 'Q-19'),
    ],
  },
];
